/**
 * SOLY-PO-001 MS-16 — pure client-side projection for the org-wide « Commandes fournisseurs »
 * read-only registre. The BE already aggregates delivery span + next deadline per order; here we
 * only filter, count and sort the working set in memory (low dogfood volume — no server paging).
 */
import type { BoardOrderDto } from './api/v2/planning'

/** 'en-cours' = the default working view (Sent + Confirmed); 'all' = every non-closed order. */
export type BoardStatusFilter = 'en-cours' | 'Draft' | 'Sent' | 'Confirmed' | 'all'

export type BoardSortKey = 'deadline' | 'sent' | 'supplier'
export type SortDir = 'asc' | 'desc'

export interface BoardFilters {
  status: BoardStatusFilter
  supplierId: string | 'all'
  search: string
  overdueOnly: boolean
  /** Delivery window overlap bounds (yyyy-MM-dd, '' = unset). */
  deliveryFrom: string
  deliveryTo: string
}

export const DEFAULT_BOARD_FILTERS: BoardFilters = {
  status: 'en-cours',
  supplierId: 'all',
  search: '',
  overdueOnly: false,
  deliveryFrom: '',
  deliveryTo: '',
}

/** An order is overdue when its next actionable date has passed (compared on yyyy-MM-dd strings). */
export function isOrderOverdue(order: BoardOrderDto, todayIso: string): boolean {
  return order.nextDeadline != null && order.nextDeadline.date < todayIso
}

export function matchesStatus(order: BoardOrderDto, status: BoardStatusFilter): boolean {
  if (status === 'all') return true
  if (status === 'en-cours') return order.status === 'Sent' || order.status === 'Confirmed'
  return order.status === status
}

export function filterBoardOrders(orders: BoardOrderDto[], filters: BoardFilters, todayIso: string): BoardOrderDto[] {
  const q = filters.search.trim().toLowerCase()
  return orders.filter((order) => {
    if (!matchesStatus(order, filters.status)) return false
    if (filters.supplierId !== 'all' && order.supplierId !== filters.supplierId) return false
    if (filters.overdueOnly && !isOrderOverdue(order, todayIso)) return false
    // Delivery-window overlap with [deliveryFrom, deliveryTo]. An order with no window is excluded
    // as soon as a bound is set (nothing to match against).
    if (filters.deliveryFrom && (order.deliveryEnd == null || order.deliveryEnd < filters.deliveryFrom)) return false
    if (filters.deliveryTo && (order.deliveryStart == null || order.deliveryStart > filters.deliveryTo)) return false
    if (q) {
      const haystack = [order.supplierName, order.supplierOrderNumber ?? '', ...order.projects.map((p) => p.clientName)]
        .join(' ').toLowerCase()
      if (!haystack.includes(q)) return false
    }
    return true
  })
}

export interface BoardCounts {
  total: number
  awaitingConfirmation: number
  overdue: number
}

/** Operational tallies for the summary band — counts, never money (this domain has no amounts). */
export function computeBoardCounts(orders: BoardOrderDto[], todayIso: string): BoardCounts {
  let awaitingConfirmation = 0
  let overdue = 0
  for (const order of orders) {
    if (order.nextDeadline?.kind === 'confirmation') awaitingConfirmation++
    if (isOrderOverdue(order, todayIso)) overdue++
  }
  return { total: orders.length, awaitingConfirmation, overdue }
}

/** Distinct suppliers present in the working set, for the filter dropdown (name-sorted). */
export function distinctSuppliers(orders: BoardOrderDto[]): Array<{ id: string; name: string }> {
  const byId = new Map<string, string>()
  for (const order of orders) {
    if (!byId.has(order.supplierId)) byId.set(order.supplierId, order.supplierName)
  }
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

const MAX_DATE = '9999-12-31'

export function sortBoardOrders(orders: BoardOrderDto[], key: BoardSortKey, dir: SortDir): BoardOrderDto[] {
  const factor = dir === 'asc' ? 1 : -1
  return [...orders].sort((a, b) => {
    let cmp: number
    if (key === 'supplier') {
      cmp = a.supplierName.localeCompare(b.supplierName)
    } else if (key === 'sent') {
      cmp = (a.sentDate ?? MAX_DATE).localeCompare(b.sentDate ?? MAX_DATE)
    } else {
      // deadline: soonest first in asc; orders without a deadline sink to the bottom regardless of dir.
      const ad = a.nextDeadline?.date
      const bd = b.nextDeadline?.date
      if (ad == null && bd == null) cmp = 0
      else if (ad == null) return 1
      else if (bd == null) return -1
      else cmp = ad.localeCompare(bd)
    }
    return cmp !== 0 ? cmp * factor : a.supplierName.localeCompare(b.supplierName)
  })
}
